/** @jsxImportSource @emotion/react */
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslate } from '@/i18n/useTranslate';
import slide1 from './images/slide-1.svg';
import slide2 from './images/slide-2.svg';
import slide3 from './images/slide-3.svg';
import slide4 from './images/slide-4.svg';
import slide5 from './images/slide-5.svg';

import { Logo } from '../Logo';
import { Dots } from './Dots';
import { Slide } from './Slide';
import { Grid } from '@/components';
import { css, styled } from '@mui/material';
import DATA_TEST_IDS from './dataTestIds';
import { testId } from '@/utils/testId';

const SliderContainer = styled('div')`
  * {
    color: white;
  }
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  .slide {
    transition: all 0.4s ease-out;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 1;
  }
  .step-in {
    transform: translateX(0);
    z-index: 1;
  }
  .step-out-left {
    transition:
      transform 2s,
      opacity 1s;
    opacity: 0;
    transform: translateX(-100%);
  }
  .step-out-right {
    transition:
      transform 2s,
      opacity 1s;
    opacity: 0;
    transform: translateX(100%);
  }
  .prepare-in-left {
    transform: translateX(-100%);
    transition: none;
    opacity: 1;
  }
  .prepare-in-right {
    opacity: 1;
    transform: translateX(100%);
    transition: none;
  }
  .prepare-out {
    transform: translateX(0);
    transition: none;
  }
`;
const ImageContainer = styled('div')`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
`;
interface SlideData {
  index: number;
  className?: string;
}
interface UseSlideReturn {
  secondSlot?: SlideData;
  firstSlot?: SlideData;
  selected: number;
  onChange: (index: number) => void;
}
const useSlides = ({
  startIndex = 0,
  total,
}: {
  startIndex: number;
  total: number;
}): UseSlideReturn => {
  const [selected, setSelected] = useState(startIndex);
  const [{ firstSlot, secondSlot }, setSlots] = useState<{
    firstSlot?: SlideData;
    secondSlot?: SlideData;
  }>({ firstSlot: { index: startIndex } });
  const onChange = useCallback(
    (index?: number) => {
      let auto = false;
      if (index === undefined) {
        auto = true;
        index = selected + 1;
        if (index >= total) {
          index = 0;
        }
      }
      setSelected(index);
      const direction = index < selected && !auto ? 'to-right' : 'to-left';
      const firstSlotStepOne = {
        className: direction === 'to-right' ? 'prepare-in-left' : 'prepare-out',
        index: direction === 'to-right' ? index : selected,
      };
      const firstSlotStepTwo = {
        className: direction === 'to-right' ? 'step-in' : 'step-out-left',
        index: direction === 'to-right' ? index : selected,
      };
      const secondSlotStepOne = {
        className:
          direction === 'to-right' ? 'prepare-out' : 'prepare-in-right',
        index: direction === 'to-right' ? selected : index,
      };
      const secondSlotStepTwo = {
        className: direction === 'to-right' ? 'step-out-right' : 'step-in',
        index: direction === 'to-right' ? selected : index,
      };
      requestAnimationFrame(() => {
        setSlots({
          firstSlot: firstSlotStepOne,
          secondSlot: secondSlotStepOne,
        });
        requestAnimationFrame(() => {
          setSlots({
            firstSlot: firstSlotStepTwo,
            secondSlot: secondSlotStepTwo,
          });
        });
      });
    },
    [selected, total]
  );
  const fnRef = useRef(onChange);
  fnRef.current = onChange;
  useEffect(() => {
    const intervalId = window.setTimeout(() => {
      fnRef.current();
    }, 10000);
    return () => window.clearTimeout(intervalId);
  }, [selected]);
  return {
    selected,
    firstSlot,
    secondSlot,
    onChange,
  };
};

export const ImageSlider: FC = () => {
  const { t } = useTranslate('auth');
  const slides = [
    {
      src: slide1,
      content: (
        <>
          <h2 dangerouslySetInnerHTML={{ __html: t('SLIDER_IMAGE_1_TITLE') }} />
          <p>{t('SLIDER_IMAGE_1_DESCRIPTION')}</p>
        </>
      ),
    },
    {
      src: slide2,
      content: (
        <>
          <h2 dangerouslySetInnerHTML={{ __html: t('SLIDER_IMAGE_2_TITLE') }} />
          <p>{t('SLIDER_IMAGE_2_DESCRIPTION')}</p>
        </>
      ),
    },
    {
      src: slide3,
      content: (
        <>
          <h2 dangerouslySetInnerHTML={{ __html: t('SLIDER_IMAGE_3_TITLE') }} />
          <p>{t('SLIDER_IMAGE_3_DESCRIPTION')}</p>
        </>
      ),
    },
    {
      src: slide4,
      content: (
        <>
          <h2 dangerouslySetInnerHTML={{ __html: t('SLIDER_IMAGE_4_TITLE') }} />
          <p>{t('SLIDER_IMAGE_4_DESCRIPTION')}</p>
        </>
      ),
    },
    {
      src: slide5,
      content: (
        <>
          <h2 dangerouslySetInnerHTML={{ __html: t('SLIDER_IMAGE_5_TITLE') }} />
          <p>{t('SLIDER_IMAGE_5_DESCRIPTION')}</p>
        </>
      ),
    },
  ];
  const { secondSlot, onChange, firstSlot, selected } = useSlides({
    startIndex: 0,
    total: slides.length,
  });
  return (
    <SliderContainer>
      <ImageContainer>
        {firstSlot && (
          <Slide
            image={slides[firstSlot.index].src}
            className={firstSlot.className}
          >
            {slides[firstSlot.index].content}
          </Slide>
        )}
        {secondSlot && (
          <Slide
            image={slides[secondSlot.index].src}
            className={secondSlot.className}
          >
            {slides[secondSlot.index].content}
          </Slide>
        )}
      </ImageContainer>
      <Grid
        container
        sx={{ position: 'absolute', top: 0, left: 0, width: '100%', zIndex: 1 }}
      >
        <Grid xsOffset={1.5} xs={9} sx={{ padding: '30px 0 0' }}>
          <Logo
            css={css`
              width: 100px;
            `}
          />
        </Grid>
      </Grid>
      <Grid
        container
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '100%',
          zIndex: 1,
        }}
      >
        <Grid xsOffset={1.5} xs={9} sx={{ padding: '0 0 68px' }}>
          <Dots
            count={slides.length}
            onChange={onChange}
            value={selected}
            data-test-id={testId(DATA_TEST_IDS.imageSlider + '_dots-control')}
          />
        </Grid>
      </Grid>
    </SliderContainer>
  );
};
